<template>
  <div
    v-if="isLoggedIn"
    class="ai-chat-container"
    :style="containerStyle"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @mousedown="handleTouchStart"
    @mousemove="handleTouchMove"
    @mouseup="handleTouchEnd"
    @mouseleave="handleTouchEnd"
  >
    <!-- Collapsed Bar -->
    <div v-if="!isExpanded" class="ai-chat-bar" @click="expandChat">
      <div class="ai-icon-wrapper">
        <van-icon name="bulb-o" class="ai-icon" />
      </div>
      <div class="ai-input-placeholder">
        <transition name="slide-fade" mode="out-in">
          <div :key="currentPlaceholderIndex" class="placeholder-item">
            {{ placeholderList[currentPlaceholderIndex] }}
          </div>
        </transition>
      </div>
    </div>

    <!-- Expanded Chat Window -->
    <van-popup
      v-model:show="isExpanded"
      position="bottom"
      :style="{ height: '100%', width: '100%' }"
      class="ai-chat-popup"
      teleport="body"
    >
      <div class="chat-window">
        <div class="chat-header">
          <div class="title-left">
            <van-icon name="bulb-o" class="header-icon" />
            <span class="title">AI小助手</span>
          </div>
          <div class="header-actions">
            <van-icon v-if="currentChatDbId" name="plus" class="history-icon" @click="startNewChat" />
            <van-icon name="clock-o" class="history-icon" @click="toggleHistoryList" />
            <van-icon name="cross" class="close-icon" @click="collapseChat" />
          </div>
        </div>

        <!-- History List Overlay -->
        <div v-if="showHistoryList" class="history-overlay">
          <div class="history-header">
            <span>历史会话</span>
            <van-icon name="cross" @click="showHistoryList = false" />
          </div>
          <div ref="historyList" class="history-content" @scroll="onHistoryScroll">
            <div
              v-for="session in sessionList"
              :key="session.user_copilot_chat_id"
              class="history-item"
              @click="selectSession(session)"
            >
              <div class="history-info">
                <div class="history-title">{{ session.chat_title || '新会话' }}</div>
                <div class="history-time">{{ formatTime(session.chat_time) }}</div>
              </div>
              <van-icon name="delete-o" class="delete-icon" @click.stop="handleDeleteSession(session)" />
            </div>
            <div v-if="sessionLoading" class="loading-more">加载中...</div>
            <div v-if="sessionList.length > 0 && sessionFinished" class="no-more">没有更多了</div>
            <div v-if="sessionList.length === 0 && !sessionLoading" class="no-history">暂无历史记录</div>
          </div>
        </div>

        <div v-show="!showHistoryList" ref="chatBody" class="chat-body">
          <div v-if="chatHistory.length === 0" class="empty-state">
            <div class="welcome-text">
              你好，我是你的AI助手。无论是系统怎么操作、流程如何处理，还是合规政策相关问题，都可以随时问我，我会结合知识库给你清晰的解答与建议。
            </div>
          </div>

          <div v-else class="message-list">
            <div v-for="(msg, index) in chatHistory" :key="index" class="message-item" :class="msg.type">
              <div v-if="msg.type === 'ai'" class="avatar">
                <van-icon name="smile-o" />
              </div>
              <div class="bubble">
                <div v-if="msg.type === 'ai'" v-html="formatMessage(msg.text)"></div>
                <div v-else>{{ msg.text }}</div>
                <div v-if="msg.loading && msg.text.length === 0" class="typing-indicator">
                  <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-footer">
          <div class="input-row" :class="{ 'focused': isFocused }">
            <div class="input-wrapper">
              <van-field
                v-model="question"
                placeholder="请输入您的问题..."
                type="textarea"
                rows="1"
                :autosize="{ maxHeight: 80, minHeight: 20 }"
                :border="false"
                class="text-input"
                @focus="isFocused = true"
                @blur="isFocused = false"
                @keyup.enter="handleAsk"
              >
                <template #button>
                   <van-icon name="guide-o" size="20" @click="handleAsk" color="#1B75BC" />
                </template>
              </van-field>
            </div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick, reactive } from 'vue';
import { Popup, Icon, Button, Toast, Field, Dialog } from 'vant';
import { streamSuperRepCopilot, saveChatHistory, getChatHistory, saveChatSession, getChatSessions, deleteChatSession } from '@/api/ai';

export default defineComponent({
  name: 'AIChatBar',
  components: {
    [Popup.name]: Popup,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Field.name]: Field,
    [Dialog.Component.name]: Dialog.Component,
  },
  setup() {
    const isLoggedIn = computed(() => {
      // pagesMp 路径下不显示 AI 悬浮球
      if (window.location.pathname.includes('/pagesMp/')) return false;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isLocalhost || !!localStorage.getItem('token');
    });
    const elWidth = 110 + 20;
    const elHeight = 66 + 10;
    const screenWidth = ref(window.innerWidth);
    const screenHeight = ref(window.innerHeight);
    
    // Position
    const left = ref(screenWidth.value - elWidth - 6);
    const top = ref(screenHeight.value - 60 - elHeight);
    
    const isExpanded = ref(false);
    const question = ref('');
    const isFocused = ref(false);
    const chatHistory = ref<any[]>([]);
    const sessionId = ref<string | null>(null);
    const isLoading = ref(false);
    
    // Dragging state
    const dragging = ref(false);
    const startX = ref(0);
    const startY = ref(0);
    const isClick = ref(false);
    
    const placeholderList = ['系统操作助手', '流程与合规问答', '知识库智能解答'];
    const currentPlaceholderIndex = ref(0);
    let placeholderTimer: any = null;
    
    const showHistoryList = ref(false);
    const sessionList = ref<any[]>([]);
    const currentChatDbId = ref<string | null>(null);
    const sessionPage = ref(1);
    const sessionPageSize = 20;
    const sessionFinished = ref(false);
    const sessionLoading = ref(false);
    const chatBody = ref<HTMLElement | null>(null);
    const historyList = ref<HTMLElement | null>(null);

    const containerStyle = computed(() => {
      return {
        left: `${left.value}px`,
        top: `${top.value}px`,
        transition: dragging.value ? 'none' : 'all 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)'
      };
    });

    const initPosition = () => {
      screenWidth.value = window.innerWidth;
      screenHeight.value = window.innerHeight;
      left.value = screenWidth.value - elWidth - 6;
      top.value = screenHeight.value - 60 - elHeight;
    };

    const formatMessage = (text: string) => {
      if (!text) return '';
      let formatted = text;

      // Support Headers
      formatted = formatted.replace(/^####\s*(.*$)/gm, '<h4 style="margin: 8px 0; font-size: 14px; font-weight: bold;">$1</h4>');
      formatted = formatted.replace(/^###\s*(.*$)/gm, '<h3 style="margin: 10px 0; font-size: 16px; font-weight: bold;">$1</h3>');
      formatted = formatted.replace(/^##\s*(.*$)/gm, '<h2 style="margin: 12px 0; font-size: 18px; font-weight: bold; color: #333;">$1</h2>');
      formatted = formatted.replace(/^#\s*(.*$)/gm, '<h1 style="margin: 12px 0; font-size: 20px; font-weight: bold; color: #333;">$1</h1>');

      // Support Horizontal Rule
      formatted = formatted.replace(/^---$/gm, '<hr style="border: 0; border-top: 1px solid #eee; margin: 12px 0;">');

      // Support Blockquotes
      formatted = formatted.replace(/^> (.*$)/gm, '<blockquote style="margin: 8px 0; padding-left: 10px; border-left: 3px solid #ddd; color: #666;">$1</blockquote>');

      // Support Bold **text**
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: bold;">$1</strong>');

      // Support Links [text](url) - Display only, no click
      formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<span style="color: #aa341b; font-weight: 500;">$1</span>');

      // Support Raw URLs - Shorten to last path segment
      formatted = formatted.replace(/(https?:\/\/[^\s<]+)/g, (match) => {
        try {
          let cleanUrl = match;
          let suffix = '';
          // Strip common trailing punctuation
          const trailing = cleanUrl.match(/[.,;!?)]+$/);
          if (trailing) {
            suffix = trailing[0];
            cleanUrl = cleanUrl.slice(0, -suffix.length);
          }

          const urlObj = new URL(cleanUrl);
          const path = urlObj.pathname;
          // Use last segment of path, or hostname if path is empty
          const segments = path.split('/').filter(s => s);
          let label = segments.length > 0 ? segments[segments.length - 1] : urlObj.hostname;

          try {
            label = decodeURIComponent(label);
          } catch (e) {
            // ignore
          }

          return `<span style="color: #1B75BC; font-weight: 500;">${label}</span>${suffix}`;
        } catch (e) {
          return match;
        }
      });

      // Support Lists
      // 1. Bullet • (Level 1 / Section Header style)
      formatted = formatted.replace(/^\s*•\s+(.*$)/gm, '<div style="margin-top: 4px; font-weight: 500; color: #333; display: flex; align-items: flex-start;"><span style="margin-right: 6px; color: #1B75BC; line-height: 1.5;">•</span><div style="flex: 1; line-height: 1.5;">$1</div></div>');

      // 2. Dash - (Level 2 / Detail Item style)
      formatted = formatted.replace(/^\s*-\s+(.*$)/gm, '<div style="padding-left: 2px; margin-top: 0; display: flex; align-items: baseline;"><span style="margin-right: 8px; color: #999; font-size: 0.4em; position: relative; top: -2px;">●</span><div style="flex: 1; color: #555; line-height: 1.5;">$1</div></div>');

      // Remove newline after list item divs to prevent extra spacing
      formatted = formatted.replace(/<\/div>\r?\n/g, '</div>');

      // Handle Paragraphs and Line Breaks
      formatted = formatted.replace(/\n\s*\n/g, '<br>');
      formatted = formatted.replace(/\n/g, '<br>');

      // Support References like <ref> or [^1]
      formatted = formatted.replace(/<ref>(.*?)<\/ref>/g, '<span class="ref-tag" style="color: #1B75BC; font-weight: bold; margin: 0 2px; font-size: 0.9em; vertical-align: super;">$1</span>');
      formatted = formatted.replace(/\[\^(\d+)\]/g, '<span class="ref-tag" style="color: #1B75BC; font-weight: bold; margin: 0 2px; font-size: 0.9em; vertical-align: super;">[$1]</span>');

      // Support visit ID links
      formatted = formatted.replace(/【会议ID：([a-zA-Z0-9_-]+)】/g, '<span class="visit-link-btn" style="display: inline-flex; align-items: center; color: #1B75BC; font-weight: bold; cursor: pointer; background: rgba(27, 117, 188, 0.1); padding: 4px 10px; border-radius: 15px; border: 1px solid rgba(27, 117, 188, 0.2); margin: 4px 0; font-size: 13px;" onclick="window.handleVisitClick && window.handleVisitClick(\'$1\')">点击前往会议</span>');

      return formatted;
    };

    const handleResize = () => {
      screenWidth.value = window.innerWidth;
      screenHeight.value = window.innerHeight;
      snapToCorner();
    };

    const handleTouchStart = (e: any) => {
      if (isExpanded.value) return;
      dragging.value = true;
      isClick.value = true;
      const touch = e.touches ? e.touches[0] : e;
      startX.value = touch.clientX - left.value;
      startY.value = touch.clientY - top.value;
    };

    const handleTouchMove = (e: any) => {
      if (!dragging.value) return;
      e.preventDefault();
      isClick.value = false;
      const touch = e.touches ? e.touches[0] : e;
      left.value = touch.clientX - startX.value;
      top.value = touch.clientY - startY.value;
    };

    const handleTouchEnd = (e: any) => {
      if (!dragging.value) return;
      dragging.value = false;
      if (isClick.value) {
        // setTimeout(() => {
        //   expandChat();
        // }, 100);
      } else {
        snapToCorner();
      }
    };

    const snapToCorner = () => {
      const margin = 6;
      const topMargin = 30;
      const bottomMargin = 60;

      const tl = { x: margin, y: margin + topMargin };
      const tr = { x: screenWidth.value - elWidth - margin, y: margin + topMargin };
      const bl = { x: margin, y: screenHeight.value - elHeight - bottomMargin };
      const br = { x: screenWidth.value - elWidth - margin, y: screenHeight.value - elHeight - bottomMargin };

      const corners = [tl, tr, bl, br];
      let minDist = Infinity;
      let target = br;

      corners.forEach(c => {
        const dist = Math.pow(c.x - left.value, 2) + Math.pow(c.y - top.value, 2);
        if (dist < minDist) {
          minDist = dist;
          target = c;
        }
      });
      left.value = target.x;
      top.value = target.y;
    };

    const startPlaceholderAnimation = () => {
      placeholderTimer = setInterval(() => {
        currentPlaceholderIndex.value = (currentPlaceholderIndex.value + 1) % placeholderList.length;
      }, 3000);
    };

    const stopPlaceholderAnimation = () => {
      if (placeholderTimer) {
        clearInterval(placeholderTimer);
        placeholderTimer = null;
      }
    };

    const expandChat = () => {
        isExpanded.value = true;
    };
    const collapseChat = () => {
        isExpanded.value = false;
    };

    const startNewChat = () => {
      if (isLoading.value) return;
      chatHistory.value = [];
      sessionId.value = null;
      currentChatDbId.value = null;
      showHistoryList.value = false;
      question.value = '';
      localStorage.setItem('ai_copilot_session_id', '');
    };

    const loadSessionList = async () => {
      if (sessionLoading.value || sessionFinished.value) return;

      sessionLoading.value = true;
      try {
        const userId = localStorage.getItem('userId') || '';
        const res: any = await getChatSessions(userId, sessionPage.value, sessionPageSize);
        const list = res.value || [];
        const totalCount = res['@odata.count'] ? Number(res['@odata.count']) : undefined;

        if (sessionPage.value === 1) {
          sessionList.value = list;
        } else {
          sessionList.value = [...sessionList.value, ...list];
        }

        if (typeof totalCount !== 'undefined') {
          if (sessionList.value.length >= totalCount) {
            sessionFinished.value = true;
          } else {
            sessionPage.value++;
            sessionFinished.value = false;
          }
        } else {
          if (list.length < sessionPageSize) {
            sessionFinished.value = true;
          } else {
            sessionPage.value++;
            sessionFinished.value = false;
          }
        }

        nextTick(() => {
          if (historyList.value && !sessionFinished.value && historyList.value.scrollHeight <= historyList.value.clientHeight) {
            loadSessionList();
          }
        });

      } catch (e: any) {
        console.error('Fetch sessions failed', e);
        sessionFinished.value = true;
      } finally {
        sessionLoading.value = false;
      }
    };

    const toggleHistoryList = () => {
      showHistoryList.value = !showHistoryList.value;
      if (showHistoryList.value) {
        sessionList.value = [];
        sessionPage.value = 1;
        sessionFinished.value = false;
        loadSessionList();
      }
    };

    const onHistoryScroll = (e: any) => {
      const target = e.target;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
        loadSessionList();
      }
    };

    const loadHistory = async () => {
      if (!currentChatDbId.value) return;
      try {
        const res: any = await getChatHistory(currentChatDbId.value!);
        const list = res.value || [];
        if (list.length > 0) {
          const lastItem = list[list.length - 1];
          if (lastItem && lastItem.session_id) {
            sessionId.value = lastItem.session_id;
          }

          chatHistory.value = list.map((item: any) => {
             // Simplified processing compared to original
             let text = item.content;
             if (item.chat_role === 'ai') {
                 try {
                     const contentObj = JSON.parse(item.content);
                     text = contentObj.text || item.content;
                 } catch (e) {
                     // ignore
                 }
             }

            return {
                type: item.chat_role,
                text: text
            };
          });

          nextTick(() => {
            if (chatBody.value) chatBody.value.scrollTop = chatBody.value.scrollHeight;
          });
        }
      } catch (e: any) {
        console.error('Load history failed', e);
      }
    };

    const selectSession = async (session: any) => {
      currentChatDbId.value = session.user_copilot_chat_id;
      showHistoryList.value = false;
      chatHistory.value = [];
      await loadHistory();
    };

    const handleDeleteSession = (session: any) => {
         Dialog.confirm({
            title: '提示',
            message: '确定要删除这条会话记录吗？',
         })
         .then(async () => {
            try {
                await deleteChatSession(session.user_copilot_chat_id);
                Toast.success('删除成功');
                sessionList.value = sessionList.value.filter(s => s.user_copilot_chat_id !== session.user_copilot_chat_id);
                if (currentChatDbId.value === session.user_copilot_chat_id) {
                    startNewChat();
                }
            } catch(e) {
                Toast.fail('删除失败');
            }
         })
         .catch(() => {});
    };

    const formatTime = (isoString: string) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const saveRemoteLog = async (role: string, content: string, refs: any[] = [], time: string | null = null) => {
      if (!content && role === 'user') return;
      if (!currentChatDbId.value) return;

      let contentToSave = content;
      if (role === 'ai') {
        contentToSave = JSON.stringify({
          text: content,
          references: refs
        });
      }

      try {
        await saveChatHistory({
          user_copilot_chat_id: currentChatDbId.value,
          session_id: sessionId.value,
          user_id: localStorage.getItem('userId'),
          chat_role: role,
          content: contentToSave || '',
          chat_time: time || new Date().toISOString()
        });
      } catch (e: any) {
        console.error('Failed to save chat log', e);
      }
    };

    const handleAsk = async () => {
      const text = question.value.trim();
      if (!text) return;

      const requestTime = new Date().toISOString();

      chatHistory.value.push({ type: 'user', text });
      question.value = '';
      isLoading.value = true;

      const aiMessage = reactive({
        type: 'ai',
        text: '',
        loading: true,
        references: []
      });
      chatHistory.value.push(aiMessage);

      nextTick(() => {
        if (chatBody.value) chatBody.value.scrollTop = chatBody.value.scrollHeight;
      });

      if (currentChatDbId.value) {
        saveRemoteLog('user', text, [], requestTime);
      }

      let buffer = '';

      try {
        await streamSuperRepCopilot({
          content: text,
          sessionId: sessionId.value,
          onChunk: (chunk: string) => {
            buffer += chunk;
            // Simplified parsing of SSE/Lines if needed, but streamBailianRequest already returns decoded chunks
            // We append buffer to text. 
            // Note: The previous code handled "data:" prefix or json parsing? 
            // The `streamBailianRequest` implementation I wrote returns decoded text chunks.
            // If the chunk is raw SSE event, we might need to parse.
            // But usually helper returns clean text. 
            // Assuming clean text for now, or accumulation
            aiMessage.text = buffer;
            nextTick(() => {
                if (chatBody.value) chatBody.value.scrollTop = chatBody.value.scrollHeight;
            });
          },
          onComplete: async () => {
              aiMessage.loading = false;
              isLoading.value = false;
              if (currentChatDbId.value) {
                  saveRemoteLog('ai', aiMessage.text);
              }
          },
          onError: (err: any) => {
             console.error(err);
             aiMessage.text += '\n[Error: Request failed]';
             aiMessage.loading = false;
             isLoading.value = false;
          }
        });
      } catch (e) {
        console.error(e);
        aiMessage.text += '\n[Error: Request failed]';
        aiMessage.loading = false;
        isLoading.value = false;
      }
    };

    onMounted(() => {
        initPosition();
        window.addEventListener('resize', handleResize);
        startPlaceholderAnimation();
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize);
        stopPlaceholderAnimation();
    });

    return {
        isExpanded,
        containerStyle,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        expandChat,
        collapseChat,
        placeholderList,
        currentPlaceholderIndex,
        startNewChat,
        toggleHistoryList,
        question,
        isFocused,
        chatHistory,
        handleAsk,
        showHistoryList,
        historyList,
        sessionList,
        onHistoryScroll,
        selectSession,
        formatTime,
        handleDeleteSession,
        sessionLoading,
        sessionFinished,
        currentChatDbId,
        chatBody,
        formatMessage,
        isLoggedIn,
        // icon names
    };
  }
});
</script>

<style lang="less" scoped>
.ai-chat-container {
  position: fixed;
  width: 130px;
  z-index: 9999;
  touch-action: none; 
}

.ai-chat-bar {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  padding: 5px 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #eee;

  &:active {
    transform: scale(0.98);
  }
}

.ai-icon-wrapper {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg,#1B75BC,#4a9fe5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.ai-icon {
  color: white;
  font-size: 22px;
}

.ai-input-placeholder {
  color: #999;
  font-size: 14px;
  flex: 1;
  height: 44px; /* Match icon height for better centering */
  line-height: 44px; /* Center text vertically */
  overflow: hidden;
  position: relative;
}

.placeholder-item {
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.5s ease;
}
.slide-fade-enter-from {
  transform: translateY(10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.chat-window {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f8fa;
}

.chat-header {
  padding: 10px 15px;
  background: linear-gradient(135deg, #1B75BC 0%, #4a9fe5 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
  // border-radius: 12px 12px 0 0;

  .title-left {
    display: flex;
    align-items: center;
    .header-icon {
      color: white;
      margin-right: 8px;
      font-size: 18px;
    }
    .title {
      font-weight: bold;
      font-size: 16px;
      color: white;
    }
  }
}

.close-icon {
  font-size: 20px;
  color: white;
  padding: 5px;
  opacity: 0.8;
}

.header-actions {
  display: flex;
  align-items: center;

  .history-icon {
    font-size: 20px;
    color: white;
    padding: 5px;
    margin-right: 10px;
    opacity: 0.9;
  }
}

.history-overlay {
  position: absolute;
  top: 44px; 
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 100;
  display: flex;
  flex-direction: column;

  .history-header {
    padding: 15px;
    font-weight: bold;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #eee;
  }

  .history-content {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }

  .history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid #f5f5f5;
    background: #fff;

    &:active {
      background: #f9f9f9;
    }

    .history-info {
      flex: 1;
      overflow: hidden;
      margin-right: 10px;
    }

    .history-title {
      font-size: 14px;
      color: #333;
      margin-bottom: 5px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
    }

    .history-time {
      font-size: 12px;
      color: #999;
      text-align: left;
    }

    .delete-icon {
      color: #ccc;
      font-size: 20px;
      padding: 5px;
    }
  }

  .no-history, .no-more, .loading-more {
    padding: 15px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #e8f4fd 0%, #f0f8ff 50%, #ffffff 70%);
}

.empty-state {
  margin-top: 10px;
  text-align: left;

  .welcome-text {
    color: #333;
    margin-bottom: 20px;
    font-size: 14px;
  }
}

.section-title {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  text-align: left;
  padding-left: 5px;
  line-height: 1.2;
}

.workflow-guide {
  font-size: 14px;
  color: #666;
  text-align: left;
  background: rgba(255, 255, 255, 0.6);
  padding: 12px;
  border-radius: 8px;
  line-height: 1.25;

  .guide-title {
    font-weight: 500;
    margin-bottom: 10px;
    color: #1B75BC;
  }

  b {
    margin: 0 2px;
    color: #333;
    font-weight: 900;
  }
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  padding: 12px;
  border-radius: 8px;

  .action-chip {
    border: 1px solid #999;
    color: #555;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    background: #fff;
    cursor: pointer;
  }
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message-item {
  display: flex;
  max-width: 100%;

  &.user {
    align-self: flex-end;
    .bubble {
      background: #1B75BC;
      color: white;
      border-radius: 12px 12px 0 12px;
    }
  }

  &.ai {
    align-self: flex-start;
    .avatar {
      width: 30px;
      height: 30px;
      background: #eee;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 8px;
      color: #666;
    }
    .bubble {
      background: white;
      color: #333;
      border-radius: 0 12px 12px 12px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      text-align: left;
    }
  }

  .bubble {
    padding: 10px 15px;
    font-size: 14px;
    line-height: 1.5;
  }
}

.chat-footer {
  padding: 10px 15px;
  background: white;
}

.input-row {
  width: 100%;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 48px;
  padding: 2px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: all 0.3s;

  &.focused {
    border-color: #1B75BC;
    box-shadow: 0 2px 12px rgba(27, 117, 188, 0.15);
  }
}

.input-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  .text-input {
    width: 100%;
    padding: 5px 10px;
    background: transparent;
    font-size: 15px;
  }
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
  height: 20px;

  .dot {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    background-color: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style>
